// React Modules
import React, { useState } from 'react'
import cn from 'classnames'
import { Field, Button } from '@insight/toolkit-react'
import { t } from '@insight/toolkit-utils'
import { FilterRange } from './components/FilterRange'
import { CategoryFilter } from './components/CategoryFilter'
import { AIDeviceTooltip } from './components/AIDeviceTooltip'
import { isDesktop } from '@insight/toolkit-utils'
import { connectToLocale } from '@insight/toolkit-react'
import { CONTENT_FACET_LABELS } from '../../shared/coveoUtils'
import { HIDE_LOGGEDOUT_PRICING_BY_SALESORG } from '../../constants'
const SEARCHABLE_FACET_KEYS_PREFIX = ['ini_mfr_name', 'ec_brand', 'topics'];

const normalizeComparableText = (value = '') =>
  value
    .toString()
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[’‘`´]/g, "'")
    .replace(/'/g, '')
    .replace(/\s+/g, ' ')
    .toLowerCase()

const labelsMatch = (facetLabel, facetTranslation) =>
  normalizeComparableText(facetLabel) ===
  normalizeComparableText(facetTranslation)

export const SearchFilter = ({
  breadcrumbs,
  setFilter,
  setRange,
  setCategory,
  showFilters,
  toggleFilters,
  instockOnly,
  facets,
  setStockFilter,
  outOfStockOnly,
  context
}) => {
  const [expanded, setShowMore] = useState({})
  const [typeaheadOptions, setTypeaheadOptions] = useState({});
  const {isHybridXEnabled, locale} = context
  const expand = (group) =>
    setShowMore({ ...expanded, [group]: !expanded[group] })
  const hideLoggedOutPricing = window?.flags?.[HIDE_LOGGEDOUT_PRICING_BY_SALESORG];
  const applyFilters = (e) => toggleFilters(e)
  const desktopView = isDesktop()
  const onTypeaheadSearch = (name, str, options) => {
    const updatedOptions = options.filter((option) =>
      option.val.toUpperCase().includes(str.toUpperCase())
    )
    setTypeaheadOptions({
      [name]: { searchTerm: str, options: [...updatedOptions] }
    })
  }

  const setOptions = (showMore, options) => {
    return showMore
      ? options.slice(0, 5)
      : options.length > 50
        ? options.slice(0, 50)
        : options
  }

  const renderFilterGroup = () => {
    if (!facets || !facets.length) return null
    return facets.map((facet, index) => {
      const state = facet.state || {}
      const values = state.values || []
      if (values.length === 0) return null
      // do not render any facet group that have all facets with count 0
      if (values.filter(({ numberOfResults }) => numberOfResults > 0).length === 0)
        return null

      const groupKey = state.field || state.facetId
      const groupLabel = CONTENT_FACET_LABELS[groupKey] || state.displayName || state.label || state.field || state.facetId
      const isSearchableFacet = SEARCHABLE_FACET_KEYS_PREFIX.some((key) => groupKey?.toLowerCase()?.includes?.(key));
      const typeaheadOption = typeaheadOptions?.[`${groupLabel}`];
      // filter empty facets / facets with zero count
      const filteredOptions = values.filter((v) => v?.numberOfResults > 0).map((v) => ({
        val: v.value,
        count: v.numberOfResults,
        selected: v.state === 'selected',
      }))
      // whether to show manufacturer searched data
      const showTypeaheadOptions =
        !!typeaheadOption?.searchTerm && isSearchableFacet
      const tempOptions = showTypeaheadOptions
        ? typeaheadOption?.options
        : filteredOptions
      const showMore = tempOptions.length > 5 && !expanded[groupKey]
      const showLess = tempOptions.length > 5 && expanded[groupKey]
      const options = setOptions(showMore, tempOptions)
      const groupType = showTypeaheadOptions &&
          typeaheadOption?.options?.length === 0
          ? 'noOptionsAvailable'
          : 'selectedFacet';
      // Change Price label to List price for E4 experience
      const finalGroupLabel = groupLabel === 'Price' && isHybridXEnabled ? 'List price' : groupLabel
      const hidePricingFilters = hideLoggedOutPricing && finalGroupLabel === 'Price'
      const isAIDeviceFacet = labelsMatch(groupLabel, t('AI Device Type'))
      if (hidePricingFilters) return null
      return (
        <section key={index} className="c-search-filter__group o-grid">
         {!(hidePricingFilters) && (
            <div className="c-search-filter__group__label o-grid__item u-1/1">
              {isAIDeviceFacet && !desktopView && <AIDeviceTooltip locale={locale} />}
              {t(finalGroupLabel)}
              {isAIDeviceFacet && desktopView && <AIDeviceTooltip locale={locale} />}
            </div>
          )}
          {isSearchableFacet && (
            <div className="o-grid__item u-1/1">
              <label className="u-hide-visually" htmlFor={`facet-search-${groupKey}`}>{`Search by ${groupLabel}`}</label>
              <input
                name={groupKey}
                id={`facet-search-${groupKey}`}
                className="c-search-filter__group__input"
                placeholder={`Search by ${groupLabel}`}
                value={typeaheadOption?.searchTerm || ""}
                onChange={(e) =>
                  onTypeaheadSearch(groupLabel, e.target.value, filteredOptions)
                }
              />
            </div>
          )}
          {renderGroupType(groupKey, options, groupType)}
          {!hidePricingFilters && showMore ? (
            <div className="o-grid__item u-1/1">
              <Button
                className="c-search-filter__group__show-more u-text-bold"
                icon="arrow-down"
                color="inline-link"
                onClick={() => expand(groupKey)}
              >
                {t('Show more')}
              </Button>
            </div>
          ) : null}
          {!hidePricingFilters && showLess ? (
            <div className="o-grid__item u-1/1">
              <Button
                className="c-search-filter__group__show-less u-text-bold"
                icon="arrow-up"
                color="inline-link"
                onClick={() => expand(groupKey)}
              >
                {t('Show less')}
              </Button>
            </div>
          ) : null}
        </section>
      )
    })
  }
  const renderGroupType = (groupName, options, groupType) => {
    switch (groupType) {
      case 'range':
      case 'price':
        return (
            <FilterRange
            groupType={groupType}
            groupName={groupName}
            setRange={setRange}
            options={options}
          />
        )
      case 'noOptionsAvailable':
        return <p className="c-search-filter__group__none">No results</p>
      default:
        return (
          <ul className="c-search-filter__group__container o-grid__item u-1/1 o-grid">
            {renderOptions(groupName, options, groupType)}
          </ul>
        )
    }
  }
  const renderOptions = (groupKey, options, groupType) => {
    if (!options || !options.length) return null
    switch (groupType) {
      case 'category':
        return (
          <CategoryFilter
            groupKey={groupKey}
            setCategory={setCategory}
            options={options}
          />
        )
      default:
        return options.map((option, index) => {
          const key = `${groupKey}:${option.val}`
          return (
            <li key={index} className="o-grid__item u-1/1">
              <Field
                name={key}
                fieldComponent="Checkbox"
                checkboxLabel={
                  option.count
                    ? `${option.label || option.val} (${option.count})`
                    : `${option.label || option.val}`
                }
                checked={option.selected || (breadcrumbs?.has?.(key) ?? false)}
                onChange={() => {
                  setFilter({ group: groupKey, ...option })
                  setTypeaheadOptions({})
                }}
              />
            </li>
          )
        })
    }
  }
  return (
    <section
      className={cn('c-search-filter', {
        'c-search-filter-show': showFilters,
      })}
    >
      <header className="o-grid">
        <div className="c-search-filter__close">
          <Button
            color="none"
            size="small"
            icon="close"
            onClick={toggleFilters}
            aria-label={t('Close')}
          />
        </div>
        <div className="o-grid__item u-1/1">
          {/* TODO: review apply filters implementation logic for mobile. Current action dismisses the filters panel and selecting an option acts the same as the desktop view */}
          <Button
            color="primary"
            className="c-search-filter__apply-filters c-button--block"
            onClick={applyFilters}
          >
            {t('Apply filters')}
          </Button>
        </div>
        {!outOfStockOnly && (
          <div className="c-search-filter__show-in-stock o-grid__item u-1/1">
            <Field
              name="in-stock-only"
              fieldComponent="Checkbox"
              checkboxLabel={t('Show in stock only')}
              checked={instockOnly}
              onChange={setStockFilter}
            />
          </div>
        )}
      </header>
      {renderFilterGroup()}
    </section>
  )
}

export default connectToLocale(SearchFilter)